import React from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { useVANQuery } from 'apollo/queries/autogenerate/hooks';
import { usePublicBankSettlementQuery } from 'apollo/queries/autogenerate/hooks-public';
import { VAN } from 'apollo/queries/autogenerate/schemas';
import TextInput, {
  TextInputProps,
} from 'shared/modules/common/components/atoms/TextInput';
import { formatYen } from 'shared/modules/usage/utils/utils';

const fallbackVANData: VAN = {
  bankName: '三井住友銀行',
  branchName: '赤坂支店',
  accountType: '普通口座',
  accountHolder: 'カ）ペイディー',
  accountNumber: '9780752',
};

type Props = {
  paidyCode?: string;
  outstandingAmount: number;
  onError?: () => void;
};

const BankTransfer = (props: Props) => {
  // Unauthenticated (Paidy Code)
  const publicBankSettlementQuery = usePublicBankSettlementQuery({
    fetchPolicy: 'network-only',
    variables: {
      paidyCode: props.paidyCode as string,
    },
    skip: !props.paidyCode,
    context: {
      uri: process.env.CONSUMER_API_HOST_GRAPHQL_PUBLIC,
    },
  });

  // Authenticated
  const vanQuery = useVANQuery({
    fetchPolicy: 'network-only',
    skip: !!props.paidyCode,
  });

  const loading = props.paidyCode
    ? publicBankSettlementQuery.loading
    : vanQuery.loading;
  const vanData = props.paidyCode
    ? publicBankSettlementQuery.data?.bankSettlement
    : vanQuery.data?.VAN;
  const bankDetails = vanData || fallbackVANData;

  if (loading) {
    return null;
  } else {
    if (!vanData) {
      props.onError?.();
    }
  }

  const fields = [
    {
      value: bankDetails.bankName,
      label: '金融機関',
      id: 'bank-name-field',
    },
    {
      value: bankDetails.branchName,
      label: '支店名',
      id: 'branch-name-field',
    },
    {
      value: `${bankDetails.accountType} ${bankDetails.accountNumber}`,
      label: '口座種別・番号',
      id: 'account-type-field',
    },
    {
      value: bankDetails.accountHolder,
      label: '口座名義人',
      id: 'account-holder-field',
    },
    {
      value: formatYen(props.outstandingAmount, 'suf'),
      label: '振込金額',
      message: '銀行振込の手数料はお客様負担となります。',
      id: 'bill-amount-field',
    },
    {
      value: '(例: ヤマダ　タロウ　09012345678)',
      label: '振込名義人',
      id: 'transfer-holder-field',
    },
  ];

  return (
    <View>
      {!vanData && (
        <View style={[style.notice, style.noticeDanger, style.marginBottom]}>
          <Text
            style={[
              style.textCenter,
              style.textBold,
              style.textTitle,
              style.textDanger,
            ]}
          >
            お客様専用振込口座が表示出来ません
          </Text>
          <Text style={[style.textSecondary, style.textDanger]}>
            大変申し訳ございませんが、時間をおいて再度お試しいただくか、お急ぎの場合は以下の銀行口座宛てにお支払いください。
          </Text>
        </View>
      )}
      <View style={[style.notice, style.marginBottom]}>
        <Text style={style.textSecondary}>
          注：お振込先の口座名義人は
          <Text style={[style.textSecondary, style.textBold]}>ペイディ</Text>
          ではなく、
          <Text style={[style.textSecondary, style.textBold]}>
            カ）ペイディー
          </Text>
          （最後に長音記号ーを追加）となりますので、お間違いのないようご確認ください。
        </Text>
      </View>
      <View style={[style.card, style.marginBottom]}>
        {fields.map((field, index) => (
          <NonEditableField
            key={index}
            label={field.label}
            value={field.value}
            message={field.message}
            style={index === fields.length - 1 && style.textInputRootLastItem}
            id={field.id}
          />
        ))}
        <Text style={style.textSecondary}>
          不明入金を回避するため、
          <Text style={[style.textSecondary, style.textBold]}>
            振込名義人は必ずお客様の氏名を入力し、続けてご登録の携帯番号をご入力
          </Text>
          ください。
        </Text>
      </View>
      <Text style={style.textSecondary}>
        利用明細票を必ずお受け取りください。{'\n'}
        銀行振込は反映に2〜3営業日程かかる場合がございます。
      </Text>
    </View>
  );
};

const NonEditableField = (
  props: Pick<TextInputProps, 'label' | 'value' | 'message' | 'style' | 'id'>
) => (
  <TextInput
    value={props.value}
    label={props.label}
    editable={false}
    message={props.message}
    style={[style.textInputRoot, props.style]}
    labelStyle={style.textInputLabel}
    inputStyle={style.textInputNativeInput}
    id={props.id}
  />
);

const style = StyleSheet.create({
  notice: {
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#586065',
    padding: 16,
    borderRadius: 8,
  },
  noticeDanger: {
    borderColor: '#FF2751',
  },
  card: {
    backgroundColor: 'white',
    padding: 16,
    borderRadius: 8,
  },
  textInputRoot: {
    marginBottom: 16,
  },
  textInputRootLastItem: {
    marginBottom: 8,
  },
  textInputLabel: {
    color: '#607D8B',
  },
  textInputNativeInput: {
    color: '#2A2E32',
    fontWeight: 'bold',
  },
  textBold: {
    fontWeight: 'bold',
  },
  textSecondary: {
    fontSize: 12,
    color: '#586065',
  },
  textCenter: {
    textAlign: 'center',
  },
  textTitle: {
    marginBottom: 8,
  },
  textDanger: {
    color: '#FF2751',
  },
  marginBottom: {
    marginBottom: 24,
  },
});

export default BankTransfer;
